import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isDeepLinkOpen, handleDeepLink, isAppInstalled, smartLinkHandler } from '../../utils/deepLinking';

/**
 * DeepLinkHandler Component
 * SECURITY: Handles deep links when app is opened via link
 * Routes to appropriate page based on deep link path
 * Works for both PWA and native Android app
 * 
 * Security features:
 * - URL validation and sanitization
 * - Path whitelisting
 * - Query parameter validation
 * - One-time processing (prevents redirect loops)
 * - Origin validation
 * - Path traversal prevention
 * - XSS prevention
 * - IDOR protection (authentication checks for sensitive paths)
 * - Resource ID validation
 */
const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const processedRef = useRef(false); // SECURITY: Prevent duplicate processing
  const processingRef = useRef(false); // SECURITY: Prevent concurrent processing

  useEffect(() => {
    // SECURITY: Wait for auth to load before processing
    if (authLoading) {
      return;
    }

    // SECURITY: Only process once per mount
    if (processedRef.current || processingRef.current) {
      return;
    }

    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const isEmailLink = urlParams.has('app_open') || urlParams.has('email_link');
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Check if user came from external source (like Google search)
    // This indicates they might want to open the app instead
    const referrer = document.referrer;
    const isExternalReferrer = referrer && 
      !referrer.includes('baakas.com.np') && 
      !referrer.includes('localhost') &&
      !referrer.includes('127.0.0.1');
    
    // Check if this is a direct visit (no referrer) or from external source
    // Note: index.html script now handles ALL mobile visits, but this serves as a fallback
    // if the index.html script didn't run or failed for some reason
    // SECURITY: DoS Protection - Validate sessionStorage usage and limit attempts
    let hasTriedAppOpen = false;
    try {
      const stored = sessionStorage.getItem('baakas_app_open_attempted');
      // SECURITY: Validate stored value to prevent injection
      hasTriedAppOpen = stored === 'true';
    } catch (error) {
      // SECURITY: If sessionStorage fails (blocked/quota exceeded/etc.), DO NOT attempt app-opening redirects.
      // In some browsers this can cause repeated navigation that looks like a reload loop.
      hasTriedAppOpen = true;
    }
    
    // Allow app opening on:
    // 1. External referrers (Google search, etc.) - if not already tried
    // 2. Direct visits (no referrer) - if not already tried by index.html script
    // This serves as a fallback to index.html script
    const shouldTryOpenApp = !hasTriedAppOpen && 
                             isMobile && 
                             !isAppInstalled() &&
                             !urlParams.has('no_redirect'); // Allow opt-out with ?no_redirect parameter

    // If user came from external source (like Google) on mobile, try to open app
    // This enables Universal Links / App Links functionality
    if (shouldTryOpenApp) {
      processingRef.current = true;
      
      try {
        // SECURITY: Validate origin before processing
        const urlObj = new URL(currentUrl);
        const hostname = urlObj.hostname.toLowerCase();
        const isAllowedDomain = hostname === 'baakas.com.np' || 
                                hostname === 'www.baakas.com.np' ||
                                (process.env.NODE_ENV === 'development' && 
                                 (hostname === 'localhost' || hostname === '127.0.0.1'));
        
        if (isAllowedDomain) {
          // SECURITY: DoS Protection - Safely set sessionStorage with error handling
          try {
            sessionStorage.setItem('baakas_app_open_attempted', 'true');
          } catch (error) {
            // SECURITY: If storage quota exceeded, continue without storing
            // This prevents DoS via storage exhaustion attacks
          }
          
          // Try to open app - smartLinkHandler will handle fallback to web
          // If app opens, user will be in app. If not, we'll continue with normal navigation
          smartLinkHandler(
            currentUrl,
            undefined,
            () => {
              // App opened successfully - mark as processed to prevent further navigation
              processedRef.current = true;
              processingRef.current = false;
            },
            () => {
              // App didn't open - continue with normal web navigation
              processingRef.current = false;
            }
          );
          
          // Give smartLinkHandler time to attempt app opening (2.5 seconds)
          // If app opens, the callback will mark as processed
          // If not, we'll continue with normal navigation
          setTimeout(() => {
            if (!processedRef.current) {
              processingRef.current = false;
            }
          }, 2500);
          
          return; // Exit early, will continue with normal flow if app doesn't open
        }
      } catch (error) {
        // SECURITY: Removed console.error to prevent information leakage
        processingRef.current = false;
      }
    }

    // If this is an email link on mobile, try to open the app first
    if (isEmailLink && isMobile && !isAppInstalled()) {
      try {
        // SECURITY: Validate origin before processing
        const urlObj = new URL(currentUrl);
        const hostname = urlObj.hostname.toLowerCase();
        const isAllowedDomain = hostname === 'baakas.com.np' || 
                                hostname === 'www.baakas.com.np' ||
                                (process.env.NODE_ENV === 'development' && 
                                 (hostname === 'localhost' || hostname === '127.0.0.1'));
        
        if (isAllowedDomain) {
          // Remove app_open parameter to avoid redirect loop
          const cleanUrl = currentUrl.replace(/[?&]app_open=true/g, '').replace(/[?&]email_link=true/g, '');
          let urlWithoutParam = cleanUrl;
          
          // Clean up URL parameters properly
          if (urlWithoutParam.includes('?')) {
            urlWithoutParam = urlWithoutParam.replace(/\?$/, '').replace(/&$/, '');
            // If only app_open was in params, remove the ? entirely
            const testParams = new URL(urlWithoutParam).searchParams;
            if (testParams.toString() === '') {
              urlWithoutParam = urlWithoutParam.split('?')[0];
            }
          }
          
          // Try to open app - smartLinkHandler will handle fallback to web
          // If app opens, user will be in app. If not, we'll continue with normal navigation
          smartLinkHandler(
            urlWithoutParam,
            undefined,
            () => {
              // App opened successfully - mark as processed to prevent further navigation
              processedRef.current = true;
            },
            () => {
              // App didn't open - remove app_open param from URL and continue normally
              // Update URL without app_open parameter
              const newUrl = new URL(urlWithoutParam);
              newUrl.searchParams.delete('app_open');
              newUrl.searchParams.delete('email_link');
              window.history.replaceState({}, '', newUrl.toString());
            }
          );
        }
      } catch (error) {
        // SECURITY: Removed console.error to prevent information leakage
        // Continue with normal processing
      }
    }

    // Check if this is a deep link open (app was opened via link)
    if (isDeepLinkOpen() || isAppInstalled()) {
      processingRef.current = true;
      
      try {
        // SECURITY: Validate origin before processing
        const currentOrigin = window.location.origin;
        
        // SECURITY: Check if URL is from allowed domain
        try {
          const urlObj = new URL(currentUrl);
          const hostname = urlObj.hostname.toLowerCase();
          const isAllowedDomain = hostname === 'baakas.com.np' || 
                                  hostname === 'www.baakas.com.np' ||
                                  (process.env.NODE_ENV === 'development' && 
                                   (hostname === 'localhost' || hostname === '127.0.0.1'));
          
          if (!isAllowedDomain) {
            // SECURITY: Removed console.error to prevent information leakage
            processedRef.current = true;
            processingRef.current = false;
            return;
          }
        } catch (error) {
          // SECURITY: Removed console.error to prevent information leakage
          processedRef.current = true;
          processingRef.current = false;
          return;
        }

        // SECURITY: Handle deep link routing with validation and IDOR protection
        const { path, params, requiresAuth, resourceId } = handleDeepLink(currentUrl, {
          currentUser: currentUser ? { uid: currentUser.uid } : null,
          checkAuth: true,
        });
        
        // SECURITY: IDOR Protection - If path requires auth but user is not authenticated
        if (requiresAuth && !currentUser) {
          // SECURITY: Removed console.warn to prevent information leakage
          // Redirect to login with return URL
          const queryString = params.toString();
          const returnUrl = path + (queryString ? `?${queryString}` : '');
          processedRef.current = true;
          processingRef.current = false;
          navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
          return;
        }

        // SECURITY: IDOR Protection - Resource access will be verified by page component
        // Removed debug log to prevent sensitive data exposure (orderId, userId)
        
        // SECURITY: Only navigate if path is different, valid, and safe
        if (path && path !== location.pathname && path !== '/') {
          // SECURITY: Additional path validation
          if (path.includes('..') || 
              path.includes('//') || 
              path.includes('<') || 
              path.includes('>') ||
              path.includes('%2e%2e') ||
              path.includes('%2f') ||
              path.length > 500) {
            // SECURITY: Removed console.error to prevent information leakage
            processedRef.current = true;
            processingRef.current = false;
            return;
          }

          // SECURITY: Validate query parameters count
          if (params.toString().split('&').length > 20) {
            // SECURITY: Removed console.error to prevent information leakage
            processedRef.current = true;
            processingRef.current = false;
            return;
          }

          // SECURITY: Build query string with validated params
          const queryString = params.toString();
          const targetPath = path + (queryString ? `?${queryString}` : '');
          
          // SECURITY: Final validation of target path
          if (targetPath.length > 2048) {
            // SECURITY: Removed console.error to prevent information leakage
            processedRef.current = true;
            processingRef.current = false;
            return;
          }
          
          // Mark as processed before navigation
          processedRef.current = true;
          processingRef.current = false;
          
          navigate(targetPath, { replace: true });
        } else {
          processedRef.current = true;
          processingRef.current = false;
        }
      } catch (error) {
        // SECURITY: Log error but don't expose details
        // SECURITY: Removed console.error to prevent information leakage
        processedRef.current = true; // Mark as processed to prevent retry loops
        processingRef.current = false;
      }
    }
  }, [navigate, location.pathname, currentUser, authLoading]);

  return null; // This component doesn't render anything
};

export default DeepLinkHandler;

